import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    LayoutDashboard, FolderKanban, GraduationCap, ShieldCheck, Sparkles, Settings, LogOut,
    ChevronDown, ChevronRight, Building2, Menu, X, HandCoins, Mail, CheckSquare, FileStack, Store, Palette, Crown, Users, BookOpen, Wrench, KeyRound, ReceiptText, Handshake, FolderHeart,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import LangSwitcher from '@/components/LangSwitcher';
import NotificationBell from '@/components/NotificationBell';
import OfflineIndicator from '@/components/OfflineIndicator';
import { useSaNotifications } from '@/hooks/useSaNotifications';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

// Main nav items (visible to everyone or based on flags)
const NAV_ITEMS = [
    { key: 'dashboard', to: '/app/dashboard', icon: LayoutDashboard },
    { key: 'projects', to: '/app/projects', icon: FolderKanban },
    { key: 'approvals', to: '/app/approvals', icon: CheckSquare },
    { key: 'grants', to: '/app/grants', icon: HandCoins },
    { key: 'courriers', to: '/app/courriers', icon: Mail },
    { key: 'ged', to: '/app/ged', icon: FileStack },
    { key: 'marketplace', to: '/app/marketplace', icon: Store },
    { key: 'orders', to: '/app/orders', icon: ReceiptText },
    { key: 'formation', to: '/app/formation', icon: GraduationCap },
    { key: 'ai', to: '/app/ai', icon: Sparkles },
    { key: 'guide', to: '/app/guide', icon: BookOpen },
];

// Admin-only collapsible group "Gestion de l'espace"
const ADMIN_GROUP_ITEMS = [
    { key: 'team', to: '/app/team', icon: Users },
    { key: 'audit', to: '/app/audit', icon: ShieldCheck },
    { key: 'branding', to: '/app/branding', icon: Palette },
    { key: 'templates', to: '/app/templates', icon: FileStack },
    { key: 'admin', to: '/app/admin', icon: Settings },
];

export default function AppLayout() {
    const { t } = useLang();
    const { profile, signOut, session } = useAuth();
    const { workspaces, currentWorkspace, selectWorkspace, accountInfo } = useWorkspace();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    const isAdmin = currentWorkspace?.is_admin || profile?.is_super_admin;
    const isSuperAdmin = !!profile?.is_super_admin;
    const canCreateWorkspace = isSuperAdmin || !!accountInfo?.is_owner;
    const { unread: saUnread } = useSaNotifications(isSuperAdmin);
    const [adrnsInfo, setAdrnsInfo] = useState(null);
    useEffect(() => {
        if (!session?.access_token) return;
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/adrns/me`, { headers: { Authorization: `Bearer ${session.access_token}` } })
            .then(r => setAdrnsInfo(r.data)).catch(() => setAdrnsInfo(null));
    }, [session?.access_token]);

    // Auto-expand admin group when route is inside it
    const adminPaths = ADMIN_GROUP_ITEMS.map(i => i.to);
    const inAdminGroup = adminPaths.some(p => location.pathname.startsWith(p));
    const [adminOpen, setAdminOpen] = useState(inAdminGroup);
    useEffect(() => { if (inAdminGroup) setAdminOpen(true); }, [inAdminGroup]);

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    const initials = (profile?.full_name || profile?.email || '?')
        .split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

    return (
        <div className="min-h-screen flex bg-background">
            <OfflineIndicator />
            {/* Sidebar */}
            <aside
                className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-surface border-r border-border transition-transform ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
                data-testid="app-sidebar"
            >
                <div className="h-full flex flex-col">
                    <div className="px-6 py-7 border-b border-border">
                        <Link to="/app/dashboard" className="flex items-center gap-3" data-testid="brand-logo">
                            <img src={currentWorkspace?.logo_url || '/brand/african-valley-logo-256.png'}
                                 alt={currentWorkspace?.logo_url ? (currentWorkspace.name || 'Workspace') : 'African Valley · Projet 360°'}
                                 className="h-11 w-11 object-contain rounded-md shrink-0"
                                 draggable={false} />
                            <div>
                                {currentWorkspace?.logo_url ? (
                                    <div className="font-heading text-lg leading-tight text-foreground">{currentWorkspace.name}</div>
                                ) : (
                                    <>
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="font-heading text-xl font-semibold tracking-tight text-foreground">African</span>
                                            <span className="font-heading text-xl font-medium text-primary">Valley</span>
                                        </div>
                                        <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5 font-body">Projet 360°</div>
                                    </>
                                )}
                            </div>
                        </Link>
                        {isSuperAdmin && (
                            <Link
                                to="/app/super-admin"
                                data-testid="nav-super-admin"
                                className="mt-4 flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-stone-950 border border-amber-500/30 hover:border-amber-400/60 transition-colors group"
                            >
                                <Crown size={16} strokeWidth={1.8} className="text-amber-400" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-amber-300 leading-tight">Super Admin</div>
                                    <div className="text-[10px] uppercase tracking-wider text-stone-400">Environnement dédié</div>
                                </div>
                                {saUnread > 0 && (
                                    <span
                                        data-testid="sa-link-badge"
                                        className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-stone-950 text-[10px] font-bold flex items-center justify-center"
                                    >
                                        {saUnread > 9 ? '9+' : saUnread}
                                    </span>
                                )}
                                <ChevronRight size={14} strokeWidth={2} className="text-stone-500 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" />
                            </Link>
                        )}
                        {adrnsInfo?.member && (
                            <Link
                                to="/app/adrns"
                                data-testid="nav-adrns"
                                className="mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-[#0e1b14] border border-emerald-500/30 hover:border-emerald-400/60 transition-colors group"
                            >
                                <Handshake size={16} strokeWidth={1.8} className="text-emerald-400" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-emerald-300 leading-tight">Espace ADRNS</div>
                                    <div className="text-[10px] uppercase tracking-wider text-emerald-100/40">Accompagnement</div>
                                </div>
                                <ChevronRight size={14} strokeWidth={2} className="text-emerald-700 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all" />
                            </Link>
                        )}
                        {adrnsInfo?.is_porteur && (
                            <Link
                                to="/app/mon-accompagnement"
                                data-testid="nav-mon-accompagnement"
                                className="mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-primary/30 hover:border-primary/60 bg-primary/5 transition-colors group"
                            >
                                <FolderHeart size={16} strokeWidth={1.8} className="text-primary" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-foreground leading-tight">Mon accompagnement</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">ADRNS · Suivi</div>
                                </div>
                                <ChevronRight size={14} strokeWidth={2} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                            </Link>
                        )}
                    </div>

                    {/* Workspace selector */}
                    <div className="px-4 py-4 border-b border-border">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border hover:bg-surface-muted transition-colors"
                                    data-testid="workspace-selector"
                                >
                                    <div className="h-9 w-9 rounded-md bg-primary-light text-primary flex items-center justify-center">
                                        <Building2 size={18} strokeWidth={1.6} />
                                    </div>
                                    <div className="flex-1 text-left min-w-0">
                                        <div className="text-sm font-medium text-foreground truncate">
                                            {currentWorkspace?.name || t('common.no_workspace')}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground truncate uppercase tracking-wider">
                                            {currentWorkspace?.industry || '—'}
                                        </div>
                                    </div>
                                    <ChevronDown size={16} strokeWidth={1.6} className="text-muted-foreground" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-64">
                                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">Espaces</DropdownMenuLabel>
                                {workspaces.length === 0 && (
                                    <DropdownMenuItem disabled>{t('common.no_workspace')}</DropdownMenuItem>
                                )}
                                {workspaces.map((w) => (
                                    <DropdownMenuItem key={w.id} onClick={() => selectWorkspace(w)} data-testid={`workspace-option-${w.id}`}>
                                        <Building2 size={14} strokeWidth={1.6} className="mr-2" /> {w.name}
                                    </DropdownMenuItem>
                                ))}
                                {canCreateWorkspace && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => navigate('/app/onboarding')} data-testid="create-workspace-menu">
                                            + {t('common.create_workspace')}
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="app-nav">
                        {NAV_ITEMS.filter(item => {
                            if (item.superAdminOnly) return isSuperAdmin;
                            if (item.adminOnly) return isAdmin;
                            return true;
                        }).map(({ key, to, icon: Icon }) => (
                            <NavLink
                                key={key}
                                to={to}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-${key}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${isActive
                                        ? 'bg-primary-light text-primary font-medium'
                                        : 'text-foreground/80 hover:bg-surface-muted hover:text-foreground'
                                    }`
                                }
                            >
                                <Icon size={18} strokeWidth={1.5} /> {t(`nav.${key}`)}
                            </NavLink>
                        ))}

                        {/* Admin-only collapsible group : "Gestion de l'espace" */}
                        {isAdmin && (
                            <div className="pt-2" data-testid="admin-group">
                                <button
                                    type="button"
                                    onClick={() => setAdminOpen(o => !o)}
                                    aria-expanded={adminOpen}
                                    data-testid="admin-group-toggle"
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${inAdminGroup
                                        ? 'text-foreground font-medium'
                                        : 'text-foreground/70 hover:bg-surface-muted hover:text-foreground'
                                    }`}
                                >
                                    <Wrench size={18} strokeWidth={1.5} />
                                    <span className="flex-1 text-left">{t('nav.admin_group')}</span>
                                    <ChevronRight
                                        size={14}
                                        strokeWidth={2}
                                        className={`transition-transform duration-300 ${adminOpen ? 'rotate-90' : ''}`}
                                    />
                                </button>
                                <div
                                    className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${adminOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                                >
                                    <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
                                        {ADMIN_GROUP_ITEMS.map(({ key, to, icon: Icon }) => (
                                            <NavLink
                                                key={key}
                                                to={to}
                                                onClick={() => setOpen(false)}
                                                data-testid={`nav-${key}`}
                                                className={({ isActive }) =>
                                                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${isActive
                                                        ? 'bg-primary-light text-primary font-medium'
                                                        : 'text-foreground/70 hover:bg-surface-muted hover:text-foreground'
                                                    }`
                                                }
                                            >
                                                <Icon size={16} strokeWidth={1.5} /> {t(`nav.${key}`)}
                                            </NavLink>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </nav>

                    <div className="border-t border-border p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                            <LangSwitcher />
                            <NotificationBell />
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors"
                                    data-testid="user-menu-trigger"
                                >
                                    <div className="h-9 w-9 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-sm font-medium">
                                        {initials}
                                    </div>
                                    <div className="flex-1 text-left min-w-0">
                                        <div className="text-sm font-medium truncate">{profile?.full_name || profile?.email}</div>
                                        <div className="text-[11px] text-muted-foreground truncate">{profile?.email}</div>
                                    </div>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onClick={() => navigate('/app/profile')} data-testid="profile-menu-link">
                                    <Users size={14} strokeWidth={1.6} className="mr-2" /> Mon profil
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleSignOut} data-testid="signout-btn">
                                    <LogOut size={14} strokeWidth={1.6} className="mr-2" /> {t('nav.signout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </aside>

            {/* Mobile toggle */}
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="lg:hidden fixed top-4 left-4 z-50 h-10 w-10 rounded-md bg-surface border border-border flex items-center justify-center shadow-soft"
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
            >
                {open ? <X size={18} /> : <Menu size={18} />}
            </button>

            <main className="flex-1 min-w-0">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-12">
                    {session?.user?.user_metadata?.must_change_password && (
                        <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="temp-password-banner">
                            <KeyRound size={16} className="text-amber-600 shrink-0" />
                            <div className="text-sm text-amber-800 flex-1">
                                <strong>Vous utilisez un mot de passe temporaire.</strong> Pour sécuriser votre compte, définissez votre propre mot de passe.
                            </div>
                            <Link to="/app/profile" className="text-sm font-medium text-amber-900 underline underline-offset-2 whitespace-nowrap" data-testid="temp-password-banner-link">
                                Changer maintenant
                            </Link>
                        </div>
                    )}
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
